import { AnyAction, Dispatch } from "redux";
import { PubnubThunkContext } from "pubnub-redux";
import { RouterState } from "connected-react-router";
import { Action as SurveyAction } from "./survey/actions";
import { State as SurveyState } from "./survey/reducer";
import { Action as DashboardAction } from "./dashboard/actions";
import { State as DashboardState } from "./dashboard/reducer";
import { Action as AddPatientAction } from "./patient/actions";
import { Action as PatientDetailAction } from "./patient/actions";
import { State as PatientDetailState } from "./patient/reducer";
import { Action as SigninAction } from "./signin/actions";
import { State as SigninState } from "./signin/reducer";
import { Action as UploadPatientsAction } from "./uploadPatients/actions";
import { State as UploadPatientsState } from "./uploadPatients/reducer";
import { Action as MessageFamilyAction } from "./messageFamily/actions";
import { State as MessageFamilyState } from "./messageFamily/reducer";
import { State as MessagePatientState } from "./messagePatient/reducer";
import { Action as MessagePatientAction } from "./messagePatient/actions";
import { Action as ScheduleVideoChatAction } from "./scheduleVideoChat/actions";
import { Action as VideoChatAction } from "./videoChat/actions";
import { State as VideoChatState } from "./videoChat/reducer";
import { Action as QuickContactAction } from "./quickContact/actions";
import { Action as FlashAction } from "./flash/actions";
import { State as FlashState } from "./flash/reducer";
import { Action as SessionAction } from "./session/actions";
import { State as SessionState } from "./session/reducer";
import { Action as OrgsAction } from "./organizations/actions";
import { State as OrgsState } from "./organizations/reducer";
import { Action as SecureMessageAction } from "./secureMessage/actions";
import { State as SecureMessageState } from "./secureMessage/reducer";
import { Action as PsMobileAction } from "./psMobileTestPage/actions";
import { State as PsMobileState } from "./psMobileTestPage/reducer";
import { MessageActionType } from "pubnub-redux";
import { Action as MessagingAction } from "./messaging/actions";
import { State as MessagingState } from "./messaging/reducer";
import { User } from "./session/types";
import { State as AttachmentValidationRulesState } from "./attachment.reducer";
import { Action as AttachmentValidationRulesAction } from "./attachment.actions";

export type RootAction =
  | AddPatientAction
  | DashboardAction
  | QuickContactAction
  | PatientDetailAction
  | ScheduleVideoChatAction
  | SecureMessageAction
  | SigninAction
  | MessageFamilyAction
  | MessagePatientAction
  | SurveyAction
  | UploadPatientsAction
  | VideoChatAction
  | FlashAction
  | SessionAction
  | OrgsAction
  | PsMobileAction
  | PubNubMessageAction
  | PubNubWorkflowAction
  | MessageHeaderAction
  | MessagingAction
  | AttachmentValidationRulesAction;

export interface RootState {
  dashboard: DashboardState;
  patientdetail: PatientDetailState;
  securemessage: SecureMessageState;
  signin: SigninState;
  messagefamily: MessageFamilyState;
  messagepatient: MessagePatientState;
  survey: SurveyState;
  videochat: VideoChatState;
  flash: FlashState;
  session: SessionState;
  orgs: OrgsState;
  router: RouterState;
  psmobile: PsMobileState;
  upload: UploadPatientsState;
  pubnubStatus: PubNubStatus;
  pubnub: PubNubState;
  message: PubNubMessage;
  workflow: PubNubWorkflow;
  messaging: MessagingState;
  attachments: AttachmentValidationRulesState;
}

export type RootContext = PubnubThunkContext;

export type RootGetState = () => RootState;
export type RootThunk<T extends AnyAction = AnyAction> = (
  _1: Dispatch<T>,
  _2: RootGetState,
  _3: RootContext
) => Promise<void> | void;
export type RootDispatch = Dispatch<RootAction>;

export interface PubNubStatus {
  isConnected: boolean;
}

export interface PubNubState {
  message: PubNubMessage;
  workflow: PubNubWorkflow;
}
export interface PubNubMessage {
  byId: Record<string, any>;
  fireUnreadCount: boolean;
  showNewMessage: boolean;
}
export interface PubNubWorkflow {
  byId: Record<string, any>;
}
export interface FamilyMember {
  id?: string;
  patientId?: string;
  name?: string;
  relation?: string;
  phone?: string;
}

export interface FamilyMemberSms {
  id: string;
  name: string;
  relation: string;
  phone: string;
  ischecked: boolean;
}

export enum NavigationStep {
  CreateAccount = 1,
  Signin = 2,
  Organization = 3,
  Overview = 4,
  PatientDetail = 5,
  SendOutreach = 6,
  MessagePatient = 7,
  MessageFamily = 8,
  VideoChatSms = 9,
  Deactivate = 10,
  VideoChatEmail = 11
}

export interface MessageTemplate {
  id: string;
  created: string;
  modified: string;
  organizationId: string;
  friendlyName: string;
  content: string;
  type: MessageTemplateType;
}

export enum MessageTemplateType {
  patient,
  family
}

export interface CommunicationHistory {
  id: string;
  patientId: string;
  userId: string;
  action: string;
  created: string;
  details: string;
  user: User;
}

export interface Page<T> {
  count: number;
  next: string;
  previous: string;
  results: T[];
}

export interface ValueType {
  value: string;
  label: string;
}

export interface PubNubMessageAction {
  type: MessageActionType;
  payload: PubNubMessagePayload;
}
export interface PubNubWorkflowAction {
  type: MessageActionType;
  payload: PubNubWorkflowPayload;
}

export interface MessageHeaderAction {
  type: MessageHeaderActionType;
  payload?: object;
}

export interface PubNubMessagePayload {
  actualChannel: string;
  channel: string;
  message: APIPubNubMsgResponse;
  publisher: string;
  subscribedChannel: string;
  subscription: string;
  timetoken: string;
}

export interface PubNubWorkflowPayload {
  actualChannel: string;
  channel: string;
  message: APIPubNubWorkflowResponse;
  publisher: string;
  subscribedChannel: string;
  subscription: string;
  timetoken: string;
}
export interface APIPubNubMsgResponse {
  event: APIPubNubMsgEvents;
  args: APIPubNubArgsObject;
  user: string;
  conversation: string;
}

export interface APIPubNubWorkflowResponse {
  event: APIPubNubMsgEvents;
  source_state: string;
  target_state: string;
  transition_uid: string;
  workflow_status_id: string;
}

//Could type this out per api response
export interface APIPubNubArgsObject {
  read_at?: string;
  message?: string;
}

export enum APIPubNubMsgEvents {
  CONVERSATION_READ = "ConversationRead",
  CONVERSATION_CREATED = "ConversationCreated",
  MESSAGE_ADDED = "MessageAdded",
  CONVERSATION_ENDED = "ConversationEnded",
  STATE_TRANSITIONED = "StateTransitioned"
}

export enum APIPubNubWorkflowEvents {
  STATE_TRANSITIONED = "StateTransitioned"
}

export enum MessageHeaderActionType {
  REMOVE_MESSAGE_BADGE = "REMOVE_MESSAGE_BADGE",
  ADD_MESSAGE_BADGE = "ADD_MESSAGE_BADGE",
  CONNECTED_EVENT = "pubnub/CONNECTED_EVENT"
}

export interface AttachmentValidationRules {
  maxFileSizeBytes: number;
  validMimeTypes: string[];
}

export interface InputFile {
  lastmodified: Date;
  name: string;
  size: number;
  type: string;
  webkitRelativePath: string;
}
